// wayai eval journey capture <conversation_id> — capture a real conversation as
// an eval JOURNEY (the full transcript) via POST /api/evals/journeys/from-conversation.
//
// Unlike `wayai eval capture` (scenario, last exchange → local YAML), a journey
// is platform-managed: it owns its scenario set and materializes its own
// single-turn eval steps server-side. So this command does NOT write a local
// file — it creates the journey on the platform and prints the result.
import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { ApiClient } from '../lib/api-client.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { UUID_RE } from '../lib/utils.js';
import { findStepBoundaries } from '@wayai/core';
import type { JourneyTranscriptTurnResponse } from '@wayai/core/contracts';

interface ParsedArgs {
  conversationId: string;
  journeyName: string | null;
  evaluatorInstructions: string | null;
}

function parseArgs(args: string[]): ParsedArgs {
  if (args.length === 0 || args[0].startsWith('-')) {
    console.error('Usage: wayai eval journey capture <conversation_id> [--name <journey_name>] [--instructions <text>]');
    process.exit(1);
  }

  const conversationId = args[0];
  if (!UUID_RE.test(conversationId)) {
    console.error(`Invalid conversation ID "${conversationId}". Expected a UUID.`);
    process.exit(1);
  }

  let journeyName: string | null = null;
  let evaluatorInstructions: string | null = null;

  for (let i = 1; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--name') {
      journeyName = args[++i] ?? '';
      if (!journeyName) { console.error('--name requires a value.'); process.exit(1); }
    } else if (arg === '--instructions') {
      evaluatorInstructions = args[++i] ?? '';
      if (!evaluatorInstructions) { console.error('--instructions requires a value.'); process.exit(1); }
    } else {
      console.error(`Unknown argument: ${arg}`);
      process.exit(1);
    }
  }

  return { conversationId, journeyName, evaluatorInstructions };
}

export async function journeyCaptureCommand(args: string[]): Promise<void> {
  // Resolve hub before parseArgs in case parseArgs rejects unknown flags.
  const hubId = resolveActiveHubId(args);
  const parsed = parseArgs(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const journeyName = parsed.journeyName ?? `Journey ${parsed.conversationId.slice(0, 8)}`;

  console.log(`Capturing conversation ${parsed.conversationId} as a journey...`);
  const result = await client.createJourneyFromConversation({
    hub_id: hubId,
    conversation_id: parsed.conversationId,
    journey_name: journeyName,
    ...(parsed.evaluatorInstructions ? { evaluator_instructions: parsed.evaluatorInstructions } : {}),
  });

  const journey = result.data.journey;
  // The platform mints step ids on each step's concluding assistant turn; count
  // steps with the same canonical finder the materializer uses.
  const stepCount = findStepBoundaries(journey.transcript as JourneyTranscriptTurnResponse[]).length;

  console.log(`\nCreated journey "${journey.journey_name}"`);
  console.log(`  id:    ${journey.journey_id}`);
  console.log(`  steps: ${stepCount}`);
  console.log('\nThe journey and its eval steps are on the platform. Run `wayai run-eval` to evaluate it.');
}
